import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseMutation } from "@/hooks/useSupabaseMutation";
import { useAuth } from "@/hooks/useAuth";
import {
  computeSubtaskProgress,
  canCloseDefect,
  type DefectStatus,
  type SubtaskProgress,
} from "@/lib/defectAutomation";

/**
 * MGM-18 (TES-481) — Unteraufgaben eines Mangels.
 *
 * Eine Unteraufgabe ist eine reale `defects`-Zeile mit gesetztem `parent_defect_id`
 * (self-referencing 1->N, Migration 293). Wiederverwendung der realen Spalten
 * (title, assigned_to, due_date, status, team_id). RLS/Audit der defects-Zeile greifen
 * unveraendert (user-JWT-scoped Client, nie service_role).
 */

export interface DefectSubtask {
  id: string;
  title: string;
  status: DefectStatus;
  assigned_to: string | null;
  due_date: string | null;
  created_at: string;
}

const SUBTASK_COLS = "id, title, status, assigned_to, due_date, created_at";

const defectSubtaskKeys = {
  all: ["defect-subtasks"] as const,
  forParent: (parentId: string) => [...defectSubtaskKeys.all, parentId] as const,
};

async function fetchSubtasks(parentId: string): Promise<DefectSubtask[]> {
  const { data, error } = await supabase
    .from("defects")
    .select(SUBTASK_COLS)
    .eq("parent_defect_id", parentId)
    .is("deleted_at", null)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data as DefectSubtask[] | null) ?? [];
}

export interface CreateSubtaskInput {
  parentId: string;
  teamId: string;
  title: string;
  assignedTo?: string | null;
  dueDate?: string | null;
}

async function createSubtask(
  input: CreateSubtaskInput,
  createdBy: string | undefined,
): Promise<void> {
  // TES-2063: assigned_group_id (Fachabteilung) ist seit Mig 362 PFLICHT (NOT NULL). Eine Unteraufgabe
  // MUSS die Fachabteilung ihres Parents erben — sonst würde der DB-Auto-Heal-Trigger
  // default_assigned_group sie der MITGLIEDERLOSEN Fallback-Gruppe "Nicht zugewiesen" zuweisen und die
  // Fachabteilung des Parents sähe den Subtask über den Gruppen-RLS-Zweig NICHT (latenter Bug, den die
  // veralteten Typen maskierten). Wir lesen die Gruppe vom Parent (RLS-erlaubt, da der Nutzer den Parent
  // sieht) und schreiben sie explizit mit.
  const { data: parent, error: parentError } = await supabase
    .from("defects")
    .select("assigned_group_id")
    .eq("id", input.parentId)
    .single();
  if (parentError) throw parentError;

  // Unteraufgabe = defects-Zeile; team_id = das des Parents (gemeinsamer Team-Scope).
  // created_by = aktueller Nutzer (analog useCreateDefectMutation) — sonst sieht der Ersteller
  // unter "limited"-RBAC seinen eigenen Subtask nicht (RLS: assigned_to OR created_by = auth.uid()).
  const { error } = await supabase.from("defects").insert({
    parent_defect_id: input.parentId,
    team_id: input.teamId,
    assigned_group_id: parent.assigned_group_id,
    title: input.title,
    assigned_to: input.assignedTo ?? null,
    due_date: input.dueDate ?? null,
    created_by: createdBy ?? null,
    status: "open",
    severity: "medium",
  });
  if (error) throw error;
}

interface UpdateSubtaskStatusInput {
  subtaskId: string;
  status: DefectStatus;
}

async function updateSubtaskStatus(input: UpdateSubtaskStatusInput): Promise<void> {
  const { error } = await supabase
    .from("defects")
    .update({ status: input.status })
    .eq("id", input.subtaskId);
  if (error) throw error;
}

export interface UseDefectSubtasksResult {
  subtasks: DefectSubtask[];
  progress: SubtaskProgress;
  /** Parent ist abschliessbar (keine offene Unteraufgabe mehr). */
  canClose: boolean;
  isLoading: boolean;
  error: Error | null;
  addSubtask: (input: Omit<CreateSubtaskInput, "parentId" | "teamId">) => void;
  setSubtaskStatus: (input: UpdateSubtaskStatusInput) => void;
  isMutating: boolean;
}

export function useDefectSubtasks(parentId: string, teamId: string): UseDefectSubtasksResult {
  const { session, user } = useAuth();
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: defectSubtaskKeys.forParent(parentId),
    queryFn: () => fetchSubtasks(parentId),
    enabled: !!parentId && !!session,
    staleTime: 30_000,
  });

  const subtasks = useMemo(() => query.data ?? [], [query.data]);
  const progress = useMemo(() => computeSubtaskProgress(subtasks), [subtasks]);
  const canClose = useMemo(() => canCloseDefect(subtasks), [subtasks]);

  const addMutation = useSupabaseMutation<void, Omit<CreateSubtaskInput, "parentId" | "teamId">>({
    mutationFn: (input) => createSubtask({ ...input, parentId, teamId }, user?.id),
    invalidateKeys: defectSubtaskKeys.forParent(parentId),
    successMessage: t("defects.subtasks.toasts.addSuccess"),
    errorMessage: t("defects.subtasks.toasts.addError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  const statusMutation = useSupabaseMutation<void, UpdateSubtaskStatusInput>({
    mutationFn: updateSubtaskStatus,
    invalidateKeys: defectSubtaskKeys.forParent(parentId),
    successMessage: t("defects.subtasks.toasts.statusSuccess"),
    errorMessage: t("defects.subtasks.toasts.statusError"),
    showErrorDetails: true,
    toastType: "sonner",
  });

  return {
    subtasks,
    progress,
    canClose,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    addSubtask: addMutation.mutate,
    setSubtaskStatus: statusMutation.mutate,
    isMutating: addMutation.isPending || statusMutation.isPending,
  };
}
